/** `x-widget: "merge-mode"` — edits `tag_merge_mode`: an object mapping a
 *  tag-category code to a tag-attribute merge mode. Rendered inline as rows:
 *    [TagCategoryAutocomplete] → [mode Select] [delete]
 *
 * Backend consumes it as `tag_merge_mode.get(<category code>)` (per-category), so
 * this is a category → mode map, not a single global mode. The three modes are
 * fixed domain values (append / overwrite / replace).
 */
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useRef, useState } from 'react';

import { TagCategoryAutocomplete } from './TagCategoryAutocomplete';

const MERGE_MODES: { value: string; label: string; help: string }[] = [
  {
    value: 'append',
    label: 'Append',
    help: 'Add new tag attributes only; keep existing attributes.',
  },
  {
    value: 'overwrite',
    label: 'Overwrite',
    help: 'Overwrite existing attributes, add new ones, and keep attributes not mentioned in the XML.',
  },
  {
    value: 'replace',
    label: 'Replace',
    help: 'Replace tag attributes completely — a 1-to-1 match with the XML.',
  },
];

interface Props {
  toId: number;
  value: unknown;
  onChange: (next: Record<string, string>) => void;
  readOnly?: boolean;
}

type Entry = { category: string; mode: string };

function asRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

function toEntries(rec: Record<string, string>): Entry[] {
  return Object.entries(rec).map(([category, mode]) => ({ category, mode }));
}

/** Collapse rows to an object; empty categories/modes dropped, last key wins. */
function toObject(entries: Entry[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { category, mode } of entries) {
    const c = category.trim();
    if (c && mode) out[c] = mode;
  }
  return out;
}

export function MergeModeWidget({ toId, value, onChange, readOnly = false }: Props) {
  const [entries, setEntries] = useState<Entry[]>(() => toEntries(asRecord(value)));
  const lastEmitted = useRef<Record<string, string>>(toObject(entries));

  useEffect(() => {
    const incoming = asRecord(value);
    if (JSON.stringify(incoming) !== JSON.stringify(lastEmitted.current)) {
      setEntries(toEntries(incoming));
      lastEmitted.current = incoming;
    }
  }, [value]);

  function commit(next: Entry[]) {
    setEntries(next);
    const obj = toObject(next);
    lastEmitted.current = obj;
    onChange(obj);
  }
  const patch = (i: number, p: Partial<Entry>) =>
    commit(entries.map((e, idx) => (idx === i ? { ...e, ...p } : e)));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
      {entries.length === 0 && (
        <Typography variant="caption" color="text.disabled">
          No per-category merge modes.
        </Typography>
      )}
      {entries.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 140 }}>
            <TagCategoryAutocomplete
              toId={toId}
              value={entry.category || null}
              onChange={(code) => patch(index, { category: code ?? '' })}
              readOnly={readOnly}
              placeholder="Tag category"
            />
          </Box>
          <Typography variant="body2" color="text.disabled" sx={{ flexShrink: 0 }}>
            →
          </Typography>
          <TextField
            select
            size="small"
            value={entry.mode || ''}
            disabled={readOnly}
            onChange={(e) => patch(index, { mode: e.target.value })}
            sx={{ flex: 1, minWidth: 150 }}
            SelectProps={{
              renderValue: (v) => MERGE_MODES.find((m) => m.value === v)?.label ?? String(v),
            }}
          >
            {MERGE_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                <Box>
                  <Typography variant="body2">{m.label}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'normal' }}
                  >
                    {m.help}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          {!readOnly && (
            <Tooltip title="Remove" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => commit(entries.filter((_, i) => i !== index))}
                sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
      {!readOnly && (
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => commit([...entries, { category: '', mode: 'append' }])}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Add category
          </Button>
        </Box>
      )}
    </Box>
  );
}
